/*
 *  * Copyright (c) 2020 SQUAREPING INC.
 *  *
 *  * SQUAREPING CONFIDENTIAL
 *  * [2019 and Beyond] SQUAREPING INC. All Rights Reserved.
 *  *
 *  * NOTICE: The source code contained herein is, and remains the property of SQUAREPING INC.
 *  * and its suppliers, if any. The intellectual and technical concepts contained herein are
 *  * proprietary to SQUAREPING INC. and its suppliers and may be covered by U.S. and Foreign Patents,
 *  * patents in process, and are protected by trade secret or copyright law.
 *  *
 *  * Dissemination of this information or reproduction of this material is strictly forbidden
 *  * anywhere in the world unless prior written permission is obtained from SQUAREPING INC.
 */

import React, { LegacyRef, useCallback, useState } from 'react';
import css from './MaterialInput.module.css';
import classNames from 'classnames';
import { Button } from 'antd';
import { EyeInvisibleOutlined, EyeOutlined } from '@ant-design/icons';

export type MaterialInputProps = React.HTMLProps<HTMLInputElement> & {
  inputRef?: LegacyRef<HTMLInputElement>;
  label?: string;
  hint?: string;
  inputClassName?: string;
  inputStyle?: any;
  prefixElement?: any;
  isRequired?: boolean;
  passwordToggleIcons?: { visible: React.ReactNode; hidden: React.ReactNode };
};

// <FAIcon
//   className={classNames(passwordVisible ? 'fa-solid fa-eye' : 'fa-solid fa-eye-slash')}
//   size={10}
//   color={'black'}
// />
const MaterialInput = (props: MaterialInputProps) => {
  const [passwordVisible, setPasswordVisible] = useState(false);
  const [focused, setFocused] = useState(false);
  const onFocus = useCallback(
    (e) => {
      setFocused(true);
      props?.onFocus?.(e);
    },
    [props.onFocus],
  );
  const onBlur = useCallback(
    (e) => {
      setFocused(false);
      props?.onBlur?.(e);
    },
    [props.onBlur],
  );

  const togglePassVisibility = useCallback(() => {
    setPasswordVisible(!passwordVisible);
  }, [passwordVisible]);

  const { className, style, label, inputClassName, inputStyle, prefixElement, hint, type, passwordToggleIcons, ...rest } = props;
  const isCollapse = focused || !!prefixElement || !!rest.value || !!rest.placeholder;
  return (
    <div style={style} className={classNames(css.box, className)}>
      <div className={classNames(css.container, focused && css.focused, !!rest.disabled && css.disabled)}>
        {!!prefixElement && <div className={css.prefix}>{prefixElement}</div>}
        <input
          {...rest}
          type={type === 'password' && passwordVisible ? 'text' : type}
          ref={rest.inputRef}
          onFocus={onFocus}
          onBlur={onBlur}
          style={inputStyle}
          className={classNames(
            css.input,
            inputClassName,
            !!prefixElement && css.withPrefix,
            type === 'password' && css.password,
          )}
          value={rest.value || ''}
        />
        {type === 'password' && (
          <Button
            onClick={togglePassVisibility}
            shape={'circle'}
            className={css.togglePasswordButton}
            type={'link'}
            icon={
              passwordVisible ? passwordToggleIcons?.hidden || <EyeInvisibleOutlined /> : passwordToggleIcons?.visible || <EyeOutlined />
            }
          />
        )}
        {!!label && (
          <div className={classNames(css.label, isCollapse && css.collapse)}>
            {label}
            {props.isRequired ? ' *' : ''}
          </div>
        )}
      </div>
      {!!hint && <div className={css.hint}>{hint}</div>}
    </div>
  );
};

export default React.memo(MaterialInput);

// export * from '@squareping/web.ui.material-input';
// export { default } from '@squareping/web.ui.material-input';
